/**
 * Numbers of the editions (NEWS-23/24): the store's currency and the active
 * locale, whole units as in the slides, and a delta shown only when it is up.
 */
import { DateTime } from 'luxon';
import type { Locales } from '@/i18n/i18n-types';
import { formatDayMonth } from '@/lib/partial-buckets';
import { getLuxonLocale } from '@/lib/utils';

/**
 * "+22%" against the previous 30 days (whole %), or `null` when the badge
 * must not show (NEWS-24): down, flat (rounds to 0%), previous 0 or missing.
 */
export function signedDelta(
  current: number,
  previous: number | null | undefined
): string | null {
  if (previous === null || previous === undefined || previous === 0) {
    return null;
  }
  const percent = Math.round((current / previous - 1) * 100);
  return percent > 0 ? `+${percent}%` : null;
}

/**
 * "R$ 38.740" in the store's currency, no cents. `currency` comes from
 * `currencyForDisplay` (`undefined` while loading → `null`, the skeleton).
 */
export function formatMoney(
  value: number,
  currency: string | undefined,
  locale: Locales
): string | null {
  if (currency === undefined) return null;
  return new Intl.NumberFormat(getLuxonLocale(locale), {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/** "3.210" (pt) / "3,210" (en). */
export function formatCount(value: number, locale: Locales): string {
  return new Intl.NumberFormat(getLuxonLocale(locale), {
    maximumFractionDigits: 0,
  }).format(value);
}

/** "3 nov" (pt) / "Nov 3" (en), in the browser's zone. */
export function formatDay(iso: string, locale: Locales): string {
  return formatDayMonth(DateTime.fromISO(iso), locale);
}
